// shareChart — hand a live chart to the device's share sheet as a picture.
//
// ⚠️ THE CHART ON SCREEN IS SVG, AND A SHARE SHEET WANTS A FILE. Copying an
// answer already carries the words and the citations, but a chart left as
// "[chart] Cutout, 30 days" — which is all the text export can honestly say
// about a picture. This turns the rendered SVG into a PNG so the thing somebody
// actually wants to text is the thing that gets sent.
//
// ⚠️ IT RASTERISES WHAT IS ON SCREEN, NOT WHAT THE DATA SAYS. That is the whole
// point — the recipient sees the chart the sender was looking at — but it means
// anything the browser has not painted yet will not appear. Callers share from a
// click, long after the chart has settled.

/** Devices that can put a file into a share sheet. Desktop Safari/Firefox cannot. */
export function canShareFiles(): boolean {
  if (typeof navigator === "undefined" || typeof navigator.canShare !== "function") return false;
  try {
    // A one-byte probe: canShare({files}) is the only honest way to ask, and it
    // throws on some engines rather than returning false.
    const probe = new File([new Blob(["x"])], "probe.png", { type: "image/png" });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/** The mark stamped on every shared chart. Same-origin, so the canvas stays clean. */
const MARK_SRC = "/brand/raisedbeef-mark.png";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error(`could not load ${src}`));
    el.src = src;
  });
}

/**
 * Stamp the brand into the bottom-right of the card.
 *
 * ⚠️ REAL ARTWORK, NOT DRAWN TEXT. A first version drew "RaisedBeef" with
 * fillText, because the cream wordmark measures 1.64:1 against white — invisible
 * on exactly the ground these PNGs get. The green mark from the brand assets is
 * the logo, at the right weight, on white.
 *
 * ⚠️ IT NEVER BLOCKS THE SHARE, and it is SAME-ORIGIN ONLY: a cross-origin draw
 * taints the canvas and toBlob() then throws, losing the picture in order to
 * brand it.
 */
async function stampMark(
  ctx: CanvasRenderingContext2D,
  cardW: number,
  cardH: number,
  pad: number,
  markH: number,
  footH: number,
): Promise<void> {
  try {
    const img = await loadImage(MARK_SRC);
    // ⚠️ POSITIONED FROM THE RULE, NOT FROM THE TEXT BASELINE. Aligning it to
    // the citation's baseline was right while the mark was small, but the
    // baseline says nothing about where the footer BEGINS — so enlarging the
    // mark pushed its top edge up through the hairline rule above the footer
    // and it sat crossed out. Measuring from the rule means no size can do
    // that: the mark is clamped to the space below the rule and centred in it.
    const footTop = cardH - footH;
    const ruleY = footTop + Math.round(footH * 0.18);
    const floor = cardH - Math.round(footH * 0.12);
    const room = floor - ruleY - Math.round(footH * 0.12);
    const h = Math.min(markH, Math.max(1, room));
    const w = (img.width / img.height) * h;
    ctx.drawImage(img, cardW - pad - w, ruleY + (floor - ruleY - h) / 2, w, h);
  } catch {
    // No mark. The caption still says what the chart is and where it came from.
  }
}

import { drawCardChart, type CardSeries } from "./cardChart";

export interface ChartCaption {
  /** What the chart is. Without it a price line is a squiggle. */
  title: string;
  /** "USDA LM_XB403 · 2026-09-02" — the citation, carried into the picture. */
  source?: string;
  /** "$375.82" — the latest print. Omitted when it would be ambiguous. */
  headline?: string;
  /** "▼ $0.41 on the day". */
  headlineDelta?: string;
  /** Direction, for colour only. */
  headlineDir?: "up" | "down" | "flat";
  /** How the value axis reads. Currency unless the chart says otherwise. */
  formatValue?: (v: number) => string;
}

/** Brand palette for the card. Matches the report surfaces. */
const CARD = {
  paper: "#FFFFFF",
  ink: "#1E4634",      // the mark's green
  inkDeep: "#14331F",  // the dark end of the header band
  rule: "#E6E4DE",
  panel: "#F6F5F1",  // the ground the chart sits on, warm rather than grey
  muted: "#5B6169",
  accent: "#E0A93C",   // the sunburst gold
} as const;

/**
 * Break `text` into lines that fit `maxWidth`, up to `maxLines`.
 *
 * ⚠️ THE TITLE USED TO RUN OFF THE CANVAS. fillText does not wrap, so a question
 * of any length was silently cut at the edge — the reader got half a sentence
 * describing the chart they were sent, which is worse than no title because it
 * looks like the product broke rather than the title being long.
 *
 * Exported for its own test: the wrapping is the part that can be wrong without
 * anyone noticing until a long title appears.
 */
export function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines = 2,
): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines: string[] = [];
  let line = "";

  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth || !line) {
      line = next;
      continue;
    }
    lines.push(line);
    line = word;
    if (lines.length === maxLines) break;
  }

  if (lines.length < maxLines && line) lines.push(line);

  // The last line carries an ellipsis only when something was actually dropped.
  const used = lines.join(" ").split(/\s+/).length;
  if (used < words.length && lines.length) {
    let last = lines[lines.length - 1];
    while (last && ctx.measureText(`${last}…`).width > maxWidth) {
      last = last.slice(0, -1).trimEnd();
    }
    lines[lines.length - 1] = `${last}…`;
  }
  return lines;
}

/**
 * The two shapes worth cutting.
 *
 * ⚠️ TWO FORMATS BECAUSE THE FEEDS CROP DIFFERENTLY, not for variety. A 1.91:1
 * card is what X, Facebook, LinkedIn and every link preview expect — and it is
 * exactly what Instagram letterboxes into a grey sandwich. A square survives
 * both but wastes half the width in a link preview. One asset cannot be right in
 * both places, so there are two.
 *
 * ⚠️ THE LAYOUT IS PROPORTIONAL, NOT TWO HARD-CODED DESIGNS. Band and footer are
 * fractions of the height, so a third size needs a row here and nothing else.
 */
export const CARD_FORMATS = {
  feed: {
    w: 1200, h: 630, label: "Facebook, X, LinkedIn",
    pad: 44, bandH: 152, footH: 118,
    eyebrow: 13, title: 31, source: 15, site: 14, mark: 68,
  },
  square: {
    // ⚠️ ITS OWN NUMBERS, NOT THE WIDE CARD'S SCALED. A single proportional
    // formula keyed on width gave the square SMALLER type than the 1200px card
    // — 1080/1200 = 0.9 — while handing it a band nearly twice as tall. The
    // header looked lost in its own space. A square is viewed differently and
    // gets sized for that.
    w: 1080, h: 1080, label: "Instagram",
    pad: 60, bandH: 268, footH: 196,
    eyebrow: 18, title: 46, source: 20, site: 18, mark: 108,
  },
} as const;

export type CardFormat = keyof typeof CARD_FORMATS;

/**
 * Render an on-screen chart into a fixed 1200x630 branded card.
 *
 * ⚠️ FIXED SIZE, NOT DERIVED FROM THE SCREEN. The first version sized the canvas
 * from the on-screen chart, so a desktop share produced roughly 1760x960 and the
 * same chart from a phone produced about 820x700 — different aspect, different
 * quality, same button. That is a screenshot with branding, not a shareable
 * asset. Every share is now identical whoever sends it.
 *
 * ⚠️ THE SVG IS RE-RENDERED AT CARD SIZE, NOT UPSCALED. A phone chart is around
 * 350px wide and the card needs 1100; blowing up a small bitmap would be visibly
 * soft. Setting a viewBox on the clone and then its width/height makes the
 * browser redraw the vector at the target size — the whole reason this works
 * from a phone at all.
 *
 * ⚠️ THE CAPTION IS THE POINT, NOT DECORATION. A chart texted to a partner or a
 * lender with nothing saying WHAT it is or WHERE it came from is the artefact
 * this product exists not to produce — worse than the text case, because a price
 * line without a label cannot even be argued with.
 */
export async function svgToPngBlob(
  svg: SVGSVGElement,
  caption: ChartCaption,
  {
    scale = 2,
    format = "feed",
    series,
    bars,
  }: { scale?: number; format?: CardFormat; series?: CardSeries[]; bars?: boolean } = {},
): Promise<Blob> {
  const F = CARD_FORMATS[format];
  const CARD_W = F.w;
  const CARD_H = F.h;
  const rect = svg.getBoundingClientRect();
  const srcW = Math.max(1, Math.round(rect.width || svg.clientWidth || 600));
  const srcH = Math.max(1, Math.round(rect.height || svg.clientHeight || 300));

  const pad = F.pad;
  const footH = F.footH;
  const plotW = CARD_W - pad * 2;
  const sans = '-apple-system, "Segoe UI", system-ui, sans-serif';

  // ⚠️ THE BAND FITS ITS CONTENT, IT IS NOT A FIXED HEIGHT. A hard 268px on the
  // square left a wall of empty green under a one-line title — the card looked
  // unfinished, and a taller title would have been clipped by the same constant.
  // Measure first, then size.
  const probe = document.createElement("canvas").getContext("2d");
  const lineH = Math.round(F.title * 1.22);
  let titleLines: string[] = [caption.title];
  if (probe) {
    probe.font = `700 ${F.title}px ${sans}`;
    titleLines = wrapLines(probe, caption.title, plotW, 2);
  }
  const eyebrowY = Math.round(F.eyebrow * 2.4);
  const titleTop = eyebrowY + Math.round(F.title * 0.95);
  const titleBandH = titleTop + (titleLines.length - 1) * lineH + Math.round(F.title * 1.05);

  // Provisional, for the split decision below. Final values follow.
  const provPlotH = CARD_H - (titleBandH + Math.round(CARD_H * 0.03)) - footH;

  // ── The wide card's dead space ──────────────────────────────────────────
  // ⚠️ A CHART SHARED FROM A PHONE IS ROUGHLY SQUARE; THE FEED CARD IS 1.9:1.
  // Centring it uniformly — the only way to keep the axis prices the right
  // shape — marooned it in the middle with enormous empty margins either side.
  // So the leftover width is not left empty: it becomes a column carrying the
  // figure at poster size, and the chart sits beside it. Nothing is stretched
  // and nothing is blank.
  //
  // ⚠️ IT SPLITS ONLY WHEN THERE IS BOTH SOMETHING TO SAY AND ROOM TO SAY IT.
  // With no headline (chartHeadline refused — see there for when) or with a
  // chart already wide enough to fill the card, the original centred layout is
  // still the right one and is what runs.
  const srcAspect = srcW / srcH;
  const gutter = Math.round(pad * 0.9);
  // Drawn from data when the series supports it; photographed otherwise.
  const native = Array.isArray(series) && series.length > 0;
  const split = native
    // ⚠️ ONLY THE WIDE CARD SPLITS. A drawn chart fills whatever box it is
    // given, so the square has no dead space to reclaim — putting a column
    // beside it there would only make the chart narrow on purpose.
    ? !!caption.headline && CARD_W / CARD_H > 1.4 && !!probe
    : !!caption.headline && !!probe
      && plotW - Math.min(plotW, provPlotH * srcAspect) > plotW * 0.3 + gutter;

  // ⚠️ THE BAND IS SIZED TO EVERYTHING IN IT, NOT JUST THE TITLE. It was
  // measured off the title alone, so when the figure went into the band the
  // change line under it hung off the bottom edge and came out sliced in half.
  // A band that fits its tallest child cannot do that whatever goes in it.
  //
  // Only the un-split card puts the figure up here; the split card gives it a
  // column of its own, so its band stays title-height.
  const headBandH = caption.headline
    ? titleTop
      + (caption.headlineDelta
          ? Math.round(F.title * 0.86) + Math.round(F.source * 1.1)
          : Math.round(F.title * 0.9))
    : 0;
  const bandH = split ? titleBandH : Math.max(titleBandH, headBandH);

  const plotTop = bandH + Math.round(CARD_H * 0.03);
  const plotH = CARD_H - plotTop - footH;

  // ⚠️ THE FIGURE COLUMN IS SIZED TO THE FIGURE, NOT TO WHATEVER IS LEFT OVER.
  // Handing it every pixel the chart could not use put a slab of white between
  // the price and the chart — the leftover simply moved rather than being used.
  // Measuring the number and giving the rest to the chart closes that gap.
  //
  // Bounded both ways: a floor so a short price still reads as a column rather
  // than a stray label, and a ceiling so a long one cannot crowd the chart out.
  let headSize = Math.round(F.title * 2.8);
  let leftW = 0;
  if (split && probe && caption.headline) {
    probe.font = `700 ${headSize}px ${sans}`;
    let need = probe.measureText(caption.headline).width;
    while (headSize > F.title && need > plotW * 0.4) {
      headSize -= 2;
      probe.font = `700 ${headSize}px ${sans}`;
      need = probe.measureText(caption.headline).width;
    }
    if (caption.headlineDelta) {
      probe.font = `600 ${Math.round(F.source * 1.5)}px ${sans}`;
      need = Math.max(need, probe.measureText(caption.headlineDelta).width);
    }
    leftW = Math.min(Math.max(need, plotW * 0.24), plotW * 0.4);
  }

  // The chart gets everything the figure did not, and is centred in it. Its own
  // size is still capped by HEIGHT — a near-square chart cannot be made to fill
  // a 1.9:1 card without stretching, which is the one thing not on offer here.
  const chartBoxX = split ? pad + leftW + gutter : pad;
  const chartBoxW = split ? plotW - leftW - gutter : plotW;
  const chartW = native ? chartBoxW : Math.min(chartBoxW, plotH * srcAspect);
  const chartX = Math.round(chartBoxX + (chartBoxW - chartW) / 2);

  // ⚠️ viewBox FIRST, THEN width/height. A Recharts <svg> carries explicit
  // pixel dimensions and no viewBox, so setting width alone stretches the frame
  // and leaves the contents at their original scale.
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("viewBox", `0 0 ${srcW} ${srcH}`);
  // ⚠️ "none" DISTORTS EVERY GLYPH INSIDE THE CHART. Stretching an SVG to fill
  // a box scales its text non-uniformly, so axis prices came out squashed. The
  // chart keeps its shape and is centred in the plot area instead; a little
  // unused space beats a card whose numbers are the wrong shape.
  clone.setAttribute("preserveAspectRatio", "xMidYMid meet");
  clone.setAttribute("width", String(chartW));
  clone.setAttribute("height", String(plotH));
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const serialized = native ? "" : new XMLSerializer().serializeToString(clone);

  const chartImg = native ? null : await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("The chart could not be rendered as an image."));
    el.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;
  });

  const canvas = document.createElement("canvas");
  canvas.width = CARD_W * scale;
  canvas.height = CARD_H * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser cannot render the chart to an image.");
  ctx.scale(scale, scale);
  ctx.textBaseline = "middle";

  ctx.fillStyle = CARD.paper;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  const band = ctx.createLinearGradient(0, 0, CARD_W, 0);
  band.addColorStop(0, CARD.inkDeep);
  band.addColorStop(1, CARD.ink);
  ctx.fillStyle = band;
  ctx.fillRect(0, 0, CARD_W, bandH);

  ctx.fillStyle = CARD.accent;
  ctx.font = `700 ${F.eyebrow}px ${sans}`;
  ctx.letterSpacing = `${(F.eyebrow * 0.2).toFixed(1)}px`;
  ctx.fillText("RAISEDBEEF MARKET INTELLIGENCE", pad, eyebrowY);
  ctx.letterSpacing = "0px";

  ctx.fillStyle = "#FFFFFF";
  ctx.font = `700 ${F.title}px ${sans}`;
  titleLines.forEach((line, i) => ctx.fillText(line, pad, titleTop + i * lineH));

  // ⚠️ THE FIGURE IS THE REASON THE CARD GETS FORWARDED. A line alone asks the
  // reader to work out where it ended; the number says it. It is omitted rather
  // than guessed whenever the series cannot name one — see chartHeadline.
  if (caption.headline && !split) {
    ctx.textAlign = "right";
    ctx.fillStyle = "#FFFFFF";
    ctx.font = `700 ${Math.round(F.title * 1.28)}px ${sans}`;
    ctx.fillText(caption.headline, CARD_W - pad, titleTop);
    if (caption.headlineDelta) {
      ctx.fillStyle =
        caption.headlineDir === "down" ? "#FF9C8F"
        : caption.headlineDir === "up" ? "#7DE0A0"
        : "#FFFFFFB0";
      ctx.font = `600 ${Math.round(F.source * 0.95)}px ${sans}`;
      ctx.fillText(caption.headlineDelta, CARD_W - pad, titleTop + Math.round(F.title * 0.86));
    }
    ctx.textAlign = "left";
  }

  // ⚠️ A PANEL, SO THE REGION READS AS FILLED RATHER THAN AS A GAP. The chart
  // cannot be widened without distorting it, so what closes the space is the
  // ground under it: the panel spans the whole region and the chart sits on it,
  // which is a chart on a card instead of a chart adrift on white.
  if (split && !native) {
    const r = Math.round(pad * 0.4);
    ctx.fillStyle = CARD.panel;
    if (typeof ctx.roundRect === "function") {
      ctx.beginPath();
      ctx.roundRect(chartBoxX, plotTop, chartBoxW, plotH, r);
      ctx.fill();
    } else {
      ctx.fillRect(chartBoxX, plotTop, chartBoxW, plotH);
    }
  }
  if (native && series) {
    drawCardChart(
      ctx,
      series,
      { x: chartX, y: plotTop, w: chartW, h: plotH },
      {
        line: CARD.ink,
        up: "#1B7F4B",
        down: "#B3402B",
        fillTop: "#1E463426",
        fillBottom: "#1E463400",
        axis: CARD.muted,
        grid: CARD.rule,
        font: sans,
        labelSize: Math.round(F.source * 1.05),
      },
      caption.formatValue ?? ((v: number) => `$${v.toFixed(2)}`),
      { bars },
    );
  } else if (chartImg) {
    ctx.drawImage(chartImg, chartX, plotTop, chartW, plotH);
  }

  // The figure at poster size, filling what would otherwise be white margin.
  if (split && caption.headline) {
    // ⚠️ SIZED TO THE COLUMN, NOT SET BY A CONSTANT. "$1,284.50" is half again
    // as wide as "$384", and a fixed size either clips the long one or wastes
    // the column on the short one. Shrink until it fits, never past the title.
    const size = headSize;
    const midY = plotTop + plotH / 2;

    ctx.fillStyle = CARD.accent;
    ctx.fillRect(pad, Math.round(midY - size * 1.15), Math.round(leftW * 0.34), 4);

    ctx.fillStyle = CARD.muted;
    ctx.font = `700 ${F.eyebrow}px ${sans}`;
    ctx.letterSpacing = `${(F.eyebrow * 0.18).toFixed(1)}px`;
    ctx.fillText("LATEST", pad, Math.round(midY - size * 0.78));
    ctx.letterSpacing = "0px";

    ctx.fillStyle = CARD.inkDeep;
    ctx.font = `700 ${size}px ${sans}`;
    ctx.fillText(caption.headline, pad, midY);

    if (caption.headlineDelta) {
      ctx.fillStyle =
        caption.headlineDir === "down" ? "#B3402B"
        : caption.headlineDir === "up" ? "#1B7F4B"
        : CARD.muted;
      ctx.font = `600 ${Math.round(F.source * 1.5)}px ${sans}`;
      ctx.fillText(caption.headlineDelta, pad, Math.round(midY + size * 0.74));
    }
  }

  const footTop = CARD_H - footH;
  ctx.fillStyle = CARD.rule;
  ctx.fillRect(pad, footTop + Math.round(footH * 0.18), plotW, 1);

  if (caption.source) {
    ctx.fillStyle = CARD.muted;
    ctx.font = `600 ${F.source}px ${sans}`;
    const src = wrapLines(ctx, caption.source, plotW - F.mark * 2.6, 1);
    ctx.fillText(src[0] ?? "", pad, footTop + Math.round(footH * 0.46));
  }
  ctx.fillStyle = "#9AA0A6";
  ctx.font = `${F.site}px ${sans}`;
  ctx.fillText("markets.raisedbeef.ai", pad, footTop + Math.round(footH * 0.70));

  await stampMark(ctx, CARD_W, CARD_H, pad, F.mark, footH);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("The chart image came back empty."))),
      "image/png",
    );
  });
}

/**
 * Share the chart inside `container`, falling back to a download.
 *
 * ⚠️ THE FALLBACK IS A DOWNLOAD, NOT SILENCE. Every step here can fail on some
 * browser — no canShare, a tainted canvas, a share sheet the user dismisses —
 * and a picture button that sometimes does nothing at all is worse than one that
 * sometimes saves a file instead.
 */
export async function shareChart(
  container: HTMLElement,
  caption: ChartCaption,
  format: CardFormat = "feed",
  series?: CardSeries[],
  bars?: boolean,
): Promise<void> {
  const svg = container.querySelector("svg");
  if (!svg) throw new Error("There is no chart here to share.");

  const blob = await svgToPngBlob(svg as SVGSVGElement, caption, { format, series, bars });
  // ⚠️ THE DIMENSIONS ARE IN THE FILENAME ON PURPOSE. A card came back square
  // from the wide option and neither of us could tell whether the format had
  // failed to apply or the wrong button had been pressed — there was nothing in
  // the artefact to check. Now the file says which shape it is, and the next
  // report answers that question without a round-trip.
  const { w, h } = CARD_FORMATS[format];
  const slug = caption.title.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase();
  const name = `${slug || "chart"}-${w}x${h}.png`;
  const file = new File([blob], name, { type: "image/png" });

  if (canShareFiles()) {
    try {
      await navigator.share({ files: [file], title: caption.title });
      return;
    } catch (err) {
      // A dismissed sheet is a decision, not a failure — and must not then
      // surprise the reader with a download they did not ask for.
      if (err instanceof DOMException && err.name === "AbortError") return;
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
